import {
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import React, { useContext, useState } from "react";

import Alert from "@material-ui/lab/Alert";
import Button from "../Button";
import { CancerScreeningResultModel } from "../../models/cancerScreeningResultModel";
import ConfirmDialog from "../ConfirmDialog";
import { LangContext } from "../../context/LangContext";
import ScreeningRequestDetail from "./ScreeningRequestDetail";
import { UserContext } from "../../context/UserContext";
import translations from "../../assets/json/translations.json";
import { useHistory } from "react-router-dom";

enum AnalysisTypeEnum {
  Pathology = 0,
  HPV = 1,
  Both = 2,
}

interface IProps {
  screeningRequest: CancerScreeningResultModel;
  cancelCancerScreeningInParent: (id: number, userSsn: string) => Promise<void>;
  createAnalysisRequestInParent: (
    id: number,
    userSsn: string,
    userName: string,
    originalUserSsn: string,
    analysisType: number
  ) => Promise<void>;
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(5),
  },
}));

const NewScreeningRequestDetails: React.FC<IProps> = ({
  screeningRequest,
  cancelCancerScreeningInParent,
  createAnalysisRequestInParent,
}) => {
  const classes = useStyles();
  const [lang] = useContext(LangContext);
  const history = useHistory();
  const theme = useTheme();

  const [openDeleteRequestDialog, setOpenDeleteRequestDialog] = useState(false);
  const [openCreateTestRequestDialog, setOpenCreateTestRequestDialog] =
    useState(false);
  const [infoLoading, setInfoLoading] = useState(false);

  const [analysisType, setAnalysisType] = useState<number | null>(null);
  const [user] = useContext(UserContext);
  const xsDown = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <Paper elevation={2} className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
            <ScreeningRequestDetail
              screeningRequest={screeningRequest}
            />
        </Grid>
        <Grid item xs={12}>
          {renderAlert()}
        </Grid>
        <Grid item xs={12}>
          {renderButtons()}
        </Grid>
      </Grid>
      {renderDialogs()}
    </Paper>
  );

  function renderButtons() {
    return (
      <Grid
        container
        spacing={2}
        justify="flex-end"
        direction="row"
        alignItems="stretch"
        wrap={xsDown ? "wrap" : "nowrap"}
      >
        <Grid item>
          <Button
            onClick={() => setOpenCreateTestRequestDialog(true)}
            variant="contained"
          >
            {translations.createTestRequest[lang]}
          </Button>
        </Grid>
        <Grid item>
          <Button
            onClick={() => setOpenDeleteRequestDialog(true)}
            color="secondary"
          >
            {translations.deleteScreeningRequest[lang]}
          </Button>
        </Grid>
        <Grid item>
          <Button
            onClick={() => history.push("/new-screening-requests")}
            variant="outlined"
          >
            {translations.back[lang]}
          </Button>
        </Grid>
      </Grid>
    );
  }

  function renderDialogs() {
    return (
      <>
        <ConfirmDialog
          open={openCreateTestRequestDialog}
          title={translations.createTestRequest[lang]}
          onCancel={() => clearDialogs()}
          onConfirm={() =>
            screeningRequest.id &&
            user.ssn &&
            analysisType !== null &&
            createAnalysisRequest(screeningRequest.id, user.ssn, user.name, screeningRequest.requestorSsn ?? user.ssn, analysisType)
          }
          confirmText={translations.create[lang]}
          text={translations.analysisType[lang]}
          disableConfirm={analysisType === null}
          loading={infoLoading}
        >
          {getCreateTestRequestDialogContent()}
        </ConfirmDialog>

        <ConfirmDialog
          open={openDeleteRequestDialog}
          title={translations.deleteScreeningRequest[lang]}
          onCancel={() => clearDialogs()}
          onConfirm={() =>
            screeningRequest.id &&
            user.ssn &&
            cancelCancerScreening(screeningRequest.id, user.ssn)
          }
          confirmText={translations.delete[lang]}
          text={translations.deleteScreeningRequestText[lang]}
        ></ConfirmDialog>
      </>
    );
  }

  function getCreateTestRequestDialogContent() {
    return (
      <Grid
        container
        spacing={0}
        direction="column"
        style={{ marginBottom: theme.spacing(0.5) }}
      >
        <Grid item xs={12}>
          <FormControlLabel
            label={translations.hpvResearch[lang]}
            onClick={() => setAnalysisType(AnalysisTypeEnum.HPV)}
            control={
              <Radio
                color="primary"
                checked={AnalysisTypeEnum.HPV === analysisType}
              />
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            label={translations.cellularResearch[lang]}
            onClick={() => setAnalysisType(AnalysisTypeEnum.Pathology)}
            control={
              <Radio
                color="primary"
                checked={AnalysisTypeEnum.Pathology === analysisType}
              />
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            label={translations.both[lang]}
            onClick={() => setAnalysisType(AnalysisTypeEnum.Both)}
            control={
              <Radio
                color="primary"
                checked={AnalysisTypeEnum.Both === analysisType}
              />
            }
          />
        </Grid>
      </Grid>
    );
  }

  function renderAlert() {
    if (screeningRequest.otherActiveRequests) {
      return (
        <Alert severity="error">
          Það eru til fleiri virkar skimunarbeiðnir fyrir þennan einstakling
        </Alert>
      );
    }
  }

  async function cancelCancerScreening(id: number, userSsn: string) {
    await cancelCancerScreeningInParent(id, userSsn);
    clearDialogs();
  }

  async function createAnalysisRequest(
    id: number,
    userSsn: string,
    userName: string,
    originalUserSsn: string,
    analysisType: number
  ) {
    setInfoLoading(true);
    await createAnalysisRequestInParent(id, userSsn, userName, originalUserSsn, analysisType);
    setInfoLoading(false);
    clearDialogs();
  }

  function clearDialogs() {
    setOpenCreateTestRequestDialog(false);
    setOpenDeleteRequestDialog(false);

    setAnalysisType(null);
  }
};

export default NewScreeningRequestDetails;
